import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { textEllipsis } from '../../constants';
import { ProjectPaymentType } from '../../enums/projectPaymentType';
import { ProjectStatus } from '../../enums/projectStatus';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { HeadCell } from '../../models/headCell';
import { Pagination } from '../../models/pagination';
import { Project } from '../../models/project';
import { CREATE_PROJECT, EDIT_PROJECT, PROJECTS } from '../../router/constants';
import { projectService } from '../../services/projectService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import { getProjectStatusStyle } from '../../utils/projectStatus';
import DeleteModal from './DeleteModal';
import FilterProjects, { FilterProjectsForm } from './FilterProjects';

export const initialProjectsColumnsData: HeadCell[] = [
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 150, padding: '11px' },
    render: (project: Project) => (
      <Box sx={getProjectStatusStyle(ProjectStatus[project.status])}>
        {ProjectStatus[project.status]}
      </Box>
    )
  },
  {
    id: 'id',
    label: 'Project #',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => project.id
  },
  {
    id: 'projectTitle',
    label: 'Title',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => (
      <Box sx={{ ...textEllipsis, maxWidth: 200 }}>{project.projectTitle}</Box>
    )
  },
  {
    id: 'category',
    label: 'Category',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 100, padding: '11px' },
    render: (project: Project) => project.category
  },
  {
    id: 'timeline',
    label: 'Timeline',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) =>
      project.startDate &&
      project.endDate &&
      format(new Date(project.startDate), 'MMM do') +
        ' - ' +
        format(new Date(project.endDate), 'MMM do')
  },
  {
    id: 'paymentType',
    label: 'Payment Type',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => ProjectPaymentType[project.paymentType]
  },
  {
    id: 'budget',
    label: 'Budget',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 100, padding: '11px' },
    render: (project: Project) =>
      project.budget || project.pricePerHour ? (
        <>
          <FormatCurrency value={project.pricePerHour ?? project.budget} />
          {ProjectPaymentType[project.paymentType] ===
          ProjectPaymentType.HOURLY_RATE
            ? '/hour'
            : null}
        </>
      ) : null
  },
  {
    id: 'amountOpen',
    label: 'Amount Open',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) =>
      project.amountOpen ? <FormatCurrency value={project.amountOpen} /> : null
  },
  {
    id: 'talent',
    label: 'Talent',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) =>
      project.talent?.firstName &&
      project.talent?.firstName + ' ' + project.talent?.lastName
  }
];

const ProjectsList = (): JSX.Element => {
  const location = useLocation();
  const initialFilterData: FilterProjectsForm = {
    startDate: null,
    endDate: null,
    projectStatuses: location.state ? ['DECLINED', 'EXPIRED'] : [],
    id: '',
    projectTitle: '',
    category: '',
    paymentType: '',
    budget: '',
    amountOpen: '',
    talentIds: []
  };
  const navigate = useNavigate();
  const [paginationProjects, setPaginationProjects] = useState<
    Pagination<Project> | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterProjectsForm>(
    initialProjectsColumnsData,
    initialFilterData
  );
  const [deleteModalData, setDeleteModalData] = useState<{
    open: boolean;
    project?: Project;
  }>({ open: false });
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();

  useEffect(() => {
    getProjects();
  }, [page, rowsPerPage, sortBy, sortType, searchTerm, filterData]);

  const getProjects = () => {
    projectService
      .getProjects(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy === 'timeline' ? 'startDate' : sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationProjects(response.data);
        setLoading(false);
      });
  };

  const onDeleteProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    setDeleteModalData({ open: true, project });
  };

  const confirmDeleteProject = () => {
    setDeleteModalData({ open: false, project: undefined });
    if (deleteModalData.project?.id) {
      projectService.deleteProject(deleteModalData.project.id).then(() => {
        getProjects();
      });
    }
  };

  const onEditProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    navigate(`${EDIT_PROJECT}/${project.id}`);
  };

  const onShowActionIcons = (project: Project) => {
    return ProjectStatus[project.status] !== ProjectStatus.COMPLETED;
  };

  const handleExport = () => {
    projectService.exportProjects(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'projects');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talentIds) {
      body = {
        ...body,
        talentIds: filterData.talentIds
          ? filterData.talentIds.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }
    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Projects</PageTitle>
        <Chip size={paginationProjects?.totalElements} />
        <Box flexGrow={1} />
        <Button sx={{ mb: 3 }} onClick={() => navigate(CREATE_PROJECT)}>
          Create a New Project
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterProjects
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationProjects?.content}
          rowClick={(project: Project) => navigate(`${PROJECTS}/${project.id}`)}
          sortProps={{ sortBy, sortType, onRequestSort }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationProjects?.totalElements
          }}
          actionProps={{
            onEdit: onEditProject,
            onDelete: onDeleteProject,
            showEditButton: onShowActionIcons,
            showDeleteButton: onShowActionIcons,
            style: { padding: '11px' }
          }}
        />
        {!loading && !paginationProjects?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No projects found!
          </Typography>
        )}
      </TableContainer>
      <DeleteModal
        modalData={deleteModalData}
        closeModal={() => setDeleteModalData({ open: false })}
        confirmModal={() => confirmDeleteProject()}
      />
    </>
  );
};

export default ProjectsList;
